/**
 * Input Validation Utilities
 * Provides reusable validators for forms across the application
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Email validation
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email || !email.trim()) {
    return { valid: false, error: "Email is required" };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return { valid: false, error: "Invalid email format" };
  }

  return { valid: true };
};

/**
 * URL validation (HTTP/HTTPS)
 */
export const validateURL = (url: string): ValidationResult => {
  if (!url || !url.trim()) {
    return { valid: false, error: "URL is required" };
  }

  try {
    const urlObj = new URL(url.trim());
    if (!["http:", "https:"].includes(urlObj.protocol)) {
      return { valid: false, error: "URL must start with http:// or https://" };
    }
    return { valid: true };
  } catch {
    return { valid: false, error: "Invalid URL format" };
  }
};

/**
 * LDAP URL validation (ldap:// or ldaps://)
 */
export const validateLDAPURL = (url: string): ValidationResult => {
  if (!url || !url.trim()) {
    return { valid: false, error: "LDAP URL is required" };
  }

  try {
    const urlObj = new URL(url.trim());
    if (!["ldap:", "ldaps:"].includes(urlObj.protocol)) {
      return {
        valid: false,
        error: "LDAP URL must start with ldap:// or ldaps://",
      };
    }

    // Validate port if present
    if (urlObj.port) {
      const port = parseInt(urlObj.port, 10);
      if (port < 1 || port > 65535) {
        return { valid: false, error: "Invalid port number (1-65535)" };
      }
    }

    return { valid: true };
  } catch {
    return { valid: false, error: "Invalid LDAP URL format" };
  }
};

/**
 * JSON string validation
 */
export const validateJSON = (jsonString: string): ValidationResult => {
  if (!jsonString || !jsonString.trim()) {
    return { valid: false, error: "JSON is required" };
  }

  try {
    JSON.parse(jsonString.trim());
    return { valid: true };
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "Invalid JSON format";
    return { valid: false, error: `Invalid JSON: ${errorMessage}` };
  }
};

/**
 * Text field with max length validation
 */
export const validateMaxLength = (
  text: string,
  maxLength: number,
  fieldName: string = "Field"
): ValidationResult => {
  if (text && text.length > maxLength) {
    return {
      valid: false,
      error: `${fieldName} must not exceed ${maxLength} characters`,
    };
  }
  return { valid: true };
};

/**
 * Text field with min length validation
 */
export const validateMinLength = (
  text: string,
  minLength: number,
  fieldName: string = "Field"
): ValidationResult => {
  if (!text || text.length < minLength) {
    return {
      valid: false,
      error: `${fieldName} must be at least ${minLength} characters`,
    };
  }
  return { valid: true };
};

/**
 * Whitespace-only validation
 */
export const validateNotWhitespaceOnly = (
  text: string,
  fieldName: string = "Field"
): ValidationResult => {
  if (!text || !text.trim()) {
    return { valid: false, error: `${fieldName} cannot be empty` };
  }
  return { valid: true };
};

/**
 * Numeric range validation (min-max)
 */
export const validateNumberRange = (
  value: number | string,
  min: number,
  max: number,
  fieldName: string = "Field"
): ValidationResult => {
  const num = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(num)) {
    return { valid: false, error: `${fieldName} must be a valid number` };
  }

  if (num < min || num > max) {
    return {
      valid: false,
      error: `${fieldName} must be between ${min} and ${max}`,
    };
  }

  return { valid: true };
};

/**
 * Required field validation
 */
export const validateRequired = (
  value: string | undefined,
  fieldName: string = "Field"
): ValidationResult => {
  if (!value || !value.trim()) {
    return { valid: false, error: `${fieldName} is required` };
  }
  return { valid: true };
};

/**
 * Port number validation
 */
export const validatePort = (port: number | string): ValidationResult => {
  const portNum = typeof port === "string" ? parseInt(port, 10) : port;

  if (isNaN(portNum)) {
    return { valid: false, error: "Port must be a valid number" };
  }

  if (portNum < 1 || portNum > 65535) {
    return { valid: false, error: "Port must be between 1 and 65535" };
  }

  return { valid: true };
};

/**
 * Date validation (DD/MM/YYYY format)
 */
export const validateDate = (
  dateString: string,
  format: string = "DD/MM/YYYY"
): ValidationResult => {
  if (!dateString || !dateString.trim()) {
    return { valid: false, error: "Date is required" };
  }

  const trimmed = dateString.trim();

  if (format === "DD/MM/YYYY") {
    const parts = trimmed.split("/");
    if (parts.length !== 3) {
      return { valid: false, error: "Date must be in DD/MM/YYYY format" };
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return { valid: false, error: "Date values must be numeric" };
    }

    if (month < 1 || month > 12) {
      return { valid: false, error: "Month must be between 1 and 12" };
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      return {
        valid: false,
        error: `Day must be between 1 and ${daysInMonth} for this month`,
      };
    }

    return { valid: true };
  }

  return { valid: false, error: `Unsupported date format: ${format}` };
};

/**
 * Date range validation - ensure end date is after start date
 */
export const validateDateRange = (
  startDateString: string,
  endDateString: string,
  format: string = "DD/MM/YYYY"
): ValidationResult => {
  // First validate individual dates
  const startValidation = validateDate(startDateString, format);
  if (!startValidation.valid) {
    return { valid: false, error: `Start date: ${startValidation.error}` };
  }

  const endValidation = validateDate(endDateString, format);
  if (!endValidation.valid) {
    return { valid: false, error: `End date: ${endValidation.error}` };
  }

  if (format === "DD/MM/YYYY") {
    const parseDate = (dateStr: string) => {
      const parts = dateStr.split("/");
      return new Date(
        parseInt(parts[2], 10),
        parseInt(parts[1], 10) - 1,
        parseInt(parts[0], 10)
      );
    };

    const startDate = parseDate(startDateString);
    const endDate = parseDate(endDateString);

    if (endDate <= startDate) {
      return {
        valid: false,
        error: "End date must be after start date",
      };
    }
  }

  return { valid: true };
};

/**
 * Alphanumeric validation (letters and numbers only)
 */
export const validateAlphanumeric = (
  text: string,
  fieldName: string = "Field"
): ValidationResult => {
  if (!text) return { valid: true };

  const alphanumericRegex = /^[a-zA-Z0-9]+$/;
  if (!alphanumericRegex.test(text)) {
    return {
      valid: false,
      error: `${fieldName} must contain only letters and numbers`,
    };
  }

  return { valid: true };
};

/**
 * Uppercase alphanumeric validation
 */
export const validateUppercaseAlphanumeric = (
  text: string,
  fieldName: string = "Field"
): ValidationResult => {
  if (!text) return { valid: true };

  const uppercaseAlphanumericRegex = /^[A-Z0-9]+$/;
  if (!uppercaseAlphanumericRegex.test(text)) {
    return {
      valid: false,
      error: `${fieldName} must contain only letters and numbers`,
    };
  }

  return { valid: true };
};

export const validateControlId = (
  controlId: string,
  existingIds: string[] = [],
  currentControlId?: string,
): ValidationResult => {
  const id = (controlId ?? "").trim();
  if (!id) return { valid: false, error: "Control ID is required" };

  const controlIdRegex = /^[A-Za-z][A-Za-z0-9._-]*$/;
  if (!controlIdRegex.test(id)) {
    return {
      valid: false,
      error:
        "Control ID must start with a letter and may contain only letters, numbers, dots, dashes, or underscores (e.g. ac-1, cisc-3.1)",
    };
  }

  const current = (currentControlId ?? "").trim().toLowerCase();
  const isDuplicate = existingIds.some(
    (existing) =>
      existing.trim().toLowerCase() === id.toLowerCase() &&
      existing.trim().toLowerCase() !== current,
  );
  if (isDuplicate) {
    return {
      valid: false,
      error: `Control ID "${id}" already exists in this catalog. Choose a unique ID.`,
    };
  }

  return { valid: true };
};

/**
 * No special characters validation
 */
export const validateNoSpecialChars = (
  text: string,
  fieldName: string = "Field",
  allowedChars: string = "-_"
): ValidationResult => {
  if (!text) return { valid: true };

  const pattern = new RegExp(`^[a-zA-Z0-9${allowedChars}\\s]*$`);
  if (!pattern.test(text)) {
    return {
      valid: false,
      error: `${fieldName} contains invalid characters`,
    };
  }

  return { valid: true };
};

/**
 * Validate enum values
 */
export const validateEnum = (
  value: string,
  allowedValues: string[],
  fieldName: string = "Field"
): ValidationResult => {
  if (!allowedValues.includes(value)) {
    return {
      valid: false,
      error: `${fieldName} must be one of: ${allowedValues.join(", ")}`,
    };
  }

  return { valid: true };
};

/**
 * Combined validator - runs multiple validations
 */
export const validateCombined = (
  validators: ValidationResult[]
): ValidationResult => {
  for (const result of validators) {
    if (!result.valid) {
      return result;
    }
  }
  return { valid: true };
};

/**
 * IPv4 address validation
 */
export const validateIpAddress = (ip: string): ValidationResult => {
  if (!ip || !ip.trim()) {
    return { valid: false, error: "IP address is required" };
  }
  const trimmed = ip.trim();
  const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const match = ipv4Regex.exec(trimmed);
  if (!match) {
    return {
      valid: false,
      error: "Invalid IP address format (e.g. 192.168.1.10)",
    };
  }
  if (match.slice(1).some((octet) => parseInt(octet, 10) > 255)) {
    return {
      valid: false,
      error: "Each IP address octet must be between 0 and 255",
    };
  }
  return { valid: true };
};
